import { css } from "@emotion/react";
import { useForm } from "@tanstack/react-form";
import { AlertCircle, CheckCircle2, Eye, EyeOff, LogIn } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAppConfigState } from "../hooks/useAppConfigState";
import { useLoginFormState } from "../hooks/useLoginFormState";
import { useSiteLayoutState } from "../hooks/useSiteLayoutState";
import { useStorefrontSettingsState } from "../hooks/useStorefrontSettingsState";
import { useTranslate } from "../hooks/useTranslate/useTranslate";

//type LoginFormProps = {};
export const LoginForm: React.FC /*<LoginFormProps>*/ = () => {
	//const theme = useGlobalTheme();
	const fieldSpacing: string = "0.75rem";
	const loginFormState = useLoginFormState();
	const appConfigState = useAppConfigState();
	const isFormDisabled =
		loginFormState.get().isWaitingForLogin ||
		loginFormState.get().isLoginSuccessful;
	const siteLayoutState = useSiteLayoutState();
	const [showPassword, setShowPassword] = useState(false);
	const AzureADSettings = appConfigState.get().appConfig?.AzureAD;
	const allowEmailLogin =
		!AzureADSettings ||
		(localStorage.getItem("storefront.login.allowemaillogin") || "false") ===
			"true";
	const storefrontSettingsState = useStorefrontSettingsState();
	const registerEnabled =
		storefrontSettingsState.get().settings?.RegisterEnabled ?? false;
	const translate = useTranslate();
	const navigate = useNavigate();
	const location = useLocation();
	const redirectParam = new URLSearchParams(location.search).get("redirect");
	const registerUrl = redirectParam
		? `/register?redirect=${encodeURIComponent(redirectParam)}`
		: "/register";
	const loginForm = useForm({
		defaultValues: {
			email: loginFormState.get().emailValue,
			password: loginFormState.get().passwordValue,
		},
		onSubmit: async ({ value }) => {
			loginFormState.setEmail(value.email);
			loginFormState.setPassword(value.password);
			await siteLayoutState.loginAsync();
		},
	});
	const getLoginFieldError = (errors: readonly unknown[]) => {
		return typeof errors[0] === "string" ? errors[0] : "";
	};

	return (
		<Card className="mx-auto w-80 sm:w-90">
			<CardHeader>
				<CardTitle>{translate("Login")}</CardTitle>
				<CardDescription>
					{AzureADSettings
						? translate("Login with your Microsoft Entra ID account")
						: translate("Enter your email to login to your account")}
				</CardDescription>
			</CardHeader>
			<CardContent>
				{allowEmailLogin && (
					<form
						noValidate
						autoComplete="off"
						onSubmit={(event) => {
							event.preventDefault();
							event.stopPropagation();
							void loginForm.handleSubmit();
						}}
					>
						{loginFormState.get().loginErrorMessage && (
							<div
								css={css`
									padding:0 .5em;
									[data-slot="alert-description"] {
										white-space: pre;
									}
								`}
							>
								<Alert variant="destructive">
									<AlertCircle />
									<AlertDescription>
										{loginFormState.get().loginErrorMessage ?? ""}
									</AlertDescription>
								</Alert>
							</div>
						)}
						{loginFormState.get().isLoginSuccessful && (
							<Alert>
								<CheckCircle2 />
								<AlertDescription>
									{translate("Login successful")}
								</AlertDescription>
							</Alert>
						)}

						<div css={css`display:flex;margin-bottom:${fieldSpacing};`}>
							<div css={css`width:100%;`}>
								<loginForm.Field
									name="email"
									validators={{
										onSubmit: ({ value }) =>
											value.trim().length === 0
												? translate("Email is required")
												: undefined,
									}}
								>
									{(field) => {
										const showError =
											field.state.meta.isTouched ||
											loginForm.state.submissionAttempts > 0;
										const error = getLoginFieldError(field.state.meta.errors);
										return (
											<Field>
												<FieldLabel
													className="mb-1 inline-block"
													htmlFor="txtEmail"
												>
													{translate("Email")}{" "}
													<span className="text-destructive">*</span>
												</FieldLabel>
												{showError && error.length > 0 && (
													<FieldError>{error}</FieldError>
												)}
												<Input
													id="txtEmail"
													placeholder={translate("Email")}
													type="text"
													autoComplete="username"
													required
													disabled={isFormDisabled}
													value={field.state.value}
													maxLength={255}
													onChange={(e) => {
														const value = e.currentTarget.value;
														field.handleChange(value);
														loginFormState.setEmail(value);
													}}
													onBlur={field.handleBlur}
													aria-invalid={showError && error.length > 0}
												/>
											</Field>
										);
									}}
								</loginForm.Field>
							</div>
						</div>
						<div css={css`display:flex;margin-bottom:${fieldSpacing};`}>
							<div css={css`width:100%;`}>
								<div className="flex items-center">
									<FieldLabel
										className="flex-1 mb-1 inline-block"
										htmlFor="txtPassword"
									>
										{translate("Password")}{" "}
										<span className="text-destructive">*</span>
									</FieldLabel>
								</div>
								<loginForm.Field
									name="password"
									validators={{
										onSubmit: ({ value }) =>
											value.trim().length === 0
												? translate("Password is required")
												: undefined,
									}}
								>
									{(field) => {
										const showError =
											field.state.meta.isTouched ||
											loginForm.state.submissionAttempts > 0;
										const error = getLoginFieldError(field.state.meta.errors);
										return (
											<Field>
												{showError && error.length > 0 && (
													<FieldError>{error}</FieldError>
												)}
												<div className="relative">
													<Input
														id="txtPassword"
														placeholder={translate("Password")}
														type={showPassword ? "text" : "password"}
														autoComplete="current-password"
														required
														disabled={isFormDisabled}
														value={field.state.value}
														maxLength={20}
														className="pr-10"
														onChange={(e) => {
															const value = e.currentTarget.value;
															field.handleChange(value);
															loginFormState.setPassword(value);
														}}
														onBlur={field.handleBlur}
														aria-invalid={showError && error.length > 0}
													/>
													<Button
														type="button"
														variant="ghost"
														size="icon"
														aria-label={translate("Toggle password visibility")}
														className="absolute right-0 top-1/2 h-8 w-8 -translate-y-1/2"
														onClick={() => setShowPassword((prev) => !prev)}
														onMouseDown={(e) => e.preventDefault()}
														disabled={isFormDisabled}
													>
														{showPassword ? <Eye /> : <EyeOff />}
													</Button>
												</div>
											</Field>
										);
									}}
								</loginForm.Field>
							</div>
						</div>

						<div>
							<div
								css={css`
								display:flex;
								margin-bottom:${fieldSpacing};
								align-items:center;
								justify-content:center;
								margin:1em 0 0 0;`}
							>
								<Button
									type="submit"
									variant={AzureADSettings ? "outline" : "default"}
									className="w-full"
									disabled={isFormDisabled || loginForm.state.isSubmitting}
								>
									{translate("Login")}
									<LogIn css={css`margin-left: 4px;`} />
								</Button>
							</div>
						</div>
						<div className="mt-2 text-center">
							<Button
								type="button"
								variant="link"
								className="flex-0"
								onClick={() => {
									navigate("/reset-password");
								}}
							>
								{translate("Forgot your password?")}
							</Button>
						</div>
					</form>
				)}
				{AzureADSettings && (
					<div
						css={css`
						display:flex;
						margin-bottom:${fieldSpacing};
						align-items:center;
						justify-content:center;
						margin:1em 0 0 0;`}
					>
						<Button
							variant="default"
							className="w-full"
							onClick={async () => {
								return Promise.resolve().then(async () => {
									await siteLayoutState.loginAzureAsync();
								});
							}}
							disabled={isFormDisabled}
						>
							{translate("Login with Microsoft")}
							<LogIn css={css`margin-left: 4px;`} />
						</Button>
					</div>
				)}
				{allowEmailLogin && (
					<div
						css={css`
						display:flex;
						margin-bottom:${fieldSpacing};
						align-items:center;
						justify-content:center;
						margin:1em 0 0 0;`}
					>
						{registerEnabled && (
							<>
								<div css={css`flex: 0 0 1em;`}></div>
								{translate("Don't have an account?")}
								<Button
									variant="link"
									css={css`
									cursor:pointer;
									background: transparent;
									border: 0;
									text-decoration: underline;
								`}
									onClick={() => {
										return Promise.resolve().then(async () => {
											await siteLayoutState.setLoginFormPopupVisible(false);
											navigate(registerUrl);
										});
									}}
								>
									{translate("Signup")}
								</Button>
							</>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
};
